import React, { useState, useEffect, useRef } from 'react';
import { StreamOverlay } from './components/StreamOverlay';
import { ConnectionStatus } from './components/ConnectionStatus';
import { KickChatService } from './services/KickChatService';
import { URLParams } from './utils/URLParams';
import './style.css';

interface Stats {
  today: number;
  last7Days: number;
  last30Days: number;
  total: number;
}

function App() {
  const [totalAmountTRY, setTotalAmountTRY] = useState(0);
  const [previousAmountTRY, setPreviousAmountTRY] = useState(0);
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [hideTimeout, setHideTimeout] = useState<NodeJS.Timeout | null>(null);

  // Stats from database
  const [stats, setStats] = useState<Stats>({ today: 0, last7Days: 0, last30Days: 0, total: 0 });

  // Kick Chat bağlantı durumu
  const [isConnected, setIsConnected] = useState(false);
  const [connectionMessage, setConnectionMessage] = useState('');
  const [kickUsername, setKickUsername] = useState<string | null>(null);
  const [showTestPanel, setShowTestPanel] = useState(true);
  const [showHelpOverlay, setShowHelpOverlay] = useState(false);
  const [helpTimeout, setHelpTimeout] = useState<NodeJS.Timeout | null>(null);

  const kickChatService = useRef<KickChatService | null>(null);

  // Fetch stats from API
  const fetchStats = async (user: string) => {
    try {
      const response = await fetch(`/api/stats?user=${encodeURIComponent(user)}`);
      if (response.ok) {
        const data = await response.json() as Stats;
        setStats(data);
        setTotalAmountTRY(data.total);
      }
    } catch (error) {
      console.error('Stats fetch error:', error);
    }
  };

  // Add spending to API
  const addSpendingToAPI = async (amount: number, user: string) => {
    try {
      const response = await fetch('/api/spending', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, user })
      });
      if (response.ok) {
        const data = await response.json();
        if (data.stats) {
          setStats(data.stats);
          // Sync totalAmountTRY with database
          setPreviousAmountTRY(totalAmountTRY);
          setTotalAmountTRY(data.stats.total);
        }
      }
    } catch (error) {
      console.error('Spending API error:', error);
    }
  };

  // Kick Chat servisini başlat
  useEffect(() => {
    const urlParams = new URLParams();
    const username = urlParams.get('user');

    // Fetch stats from database on load
    const user = username || 'test';
    fetchStats(user);

    if (username) {
      setKickUsername(username);
      setShowTestPanel(false); // URL'de user varsa test panelini gizle

      // Kick Chat servisini başlat
      kickChatService.current = new KickChatService(username);
      kickChatService.current.setCallbacks({
        onAmountChange: (newAmount, prevAmount) => {
          const addedAmount = newAmount - prevAmount;
          // Add to database - totalAmountTRY will be updated from API response
          if (addedAmount > 0 && username) {
            addSpendingToAPI(addedAmount, username);
          }
          showOverlay();
        },
        onConnectionChange: (connected, message) => {
          setIsConnected(connected);
          setConnectionMessage(message || '');
        },
        onStatusShow: () => {
          // Durum komutu için overlay'i göster
          showOverlay();
        },
        onHelpShow: () => {
          // Yardım overlay'ini göster
          if (helpTimeout) clearTimeout(helpTimeout);
          setShowHelpOverlay(true);
          const timeout = setTimeout(() => setShowHelpOverlay(false), 8000);
          setHelpTimeout(timeout);
        },
        onUndo: async () => {
          // Son harcamayı geri al
          try {
            const response = await fetch(`/api/spending?user=${encodeURIComponent(username)}`, {
              method: 'DELETE'
            });
            if (response.ok) {
              const data = await response.json();
              if (data.stats) {
                setStats(data.stats);
                setPreviousAmountTRY(totalAmountTRY);
                setTotalAmountTRY(data.stats.total);
              }
              showOverlay();
            }
          } catch (error) {
            console.error('Undo error:', error);
          }
        }
      });

      kickChatService.current.initialize();
    } else {
      setShowTestPanel(true); // URL'de user yoksa test panelini göster
      setConnectionMessage('Test modu - URL\'ye ?user=username ekleyin');
    }

    // Cleanup
    return () => {
      if (kickChatService.current) {
        kickChatService.current.disconnect();
      }
      if (hideTimeout) {
        clearTimeout(hideTimeout);
      }
    };
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTimeout) {
        clearTimeout(hideTimeout);
      }
    };
  }, [hideTimeout]);

  const showOverlay = () => {
    // Önceki timeout'u temizle
    if (hideTimeout) {
      clearTimeout(hideTimeout);
    }

    // Overlay'i göster
    setIsOverlayVisible(true);

    // 10 saniye sonra gizle
    const newTimeout = setTimeout(() => {
      setIsOverlayVisible(false);
    }, 10000);

    setHideTimeout(newTimeout);
  };

  const addTRY = (amount: number) => {
    const newAmount = totalAmountTRY + amount;
    setPreviousAmountTRY(totalAmountTRY);
    setTotalAmountTRY(newAmount);
    // Add to database - test mode uses 'test' user
    addSpendingToAPI(amount, kickUsername || 'test');
    showOverlay();
  };

  const resetSpending = async () => {
    const user = kickUsername || 'test';
    try {
      await fetch(`/api/spending?user=${encodeURIComponent(user)}`, { method: 'DELETE' });
      await fetchStats(user);
      setPreviousAmountTRY(totalAmountTRY);
      showOverlay();
    } catch (error) {
      console.error('Reset error:', error);
    }
  };

  return (
    <div className="min-h-screen text-white relative">
      {/* Bağlantı Durumu */}
      <ConnectionStatus
        isConnected={isConnected}
        message={connectionMessage}
        username={kickUsername || undefined}
      />

      {/* Ana Kontrol Paneli - Sadece Test için */}
      {showTestPanel && (
        <div className="fixed top-4 left-4 z-40">
          <div className="bg-slate-800/90 backdrop-blur-md rounded-xl p-4 border border-slate-600/50 shadow-xl min-w-[300px]">
            {/* Mevcut Toplam */}
            <div className="mb-4 text-center">
              <div className="text-sm text-slate-400 mb-1">Toplam Harcama</div>
              <div className="text-2xl font-bold text-emerald-400">
                ₺{totalAmountTRY.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>

            {/* TRY Ekleme */}
            <div className="mb-4">
              <h3 className="text-sm font-medium mb-2 text-blue-400">🇹🇷 TRY Ekle</h3>
              <div className="grid grid-cols-3 gap-2">
                <button onClick={() => addTRY(10)} className="bg-blue-600 hover:bg-blue-700 px-3 py-2 rounded text-sm">₺10</button>
                <button onClick={() => addTRY(50)} className="bg-blue-600 hover:bg-blue-700 px-3 py-2 rounded text-sm">₺50</button>
                <button onClick={() => addTRY(100)} className="bg-blue-600 hover:bg-blue-700 px-3 py-2 rounded text-sm">₺100</button>
              </div>
            </div>

            {/* Test ve Reset */}
            <div className="grid grid-cols-2 gap-2">
              <button onClick={showOverlay} className="bg-purple-600 hover:bg-purple-700 px-3 py-2 rounded text-sm">🎬 Test</button>
              <button onClick={resetSpending} className="bg-slate-600 hover:bg-slate-500 px-3 py-2 rounded text-sm">🔄 Reset</button>
            </div>
          </div>
        </div>
      )}

      {/* Stream Overlay */}
      <StreamOverlay
        amountTRY={totalAmountTRY}
        previousAmountTRY={previousAmountTRY}
        isVisible={isOverlayVisible}
        title="Toplam Harcama"
        showChange={true}
        todaySpending={stats.today}
        last7Days={stats.last7Days}
        last30Days={stats.last30Days}
        onAnimationComplete={() => console.log('Overlay animasyonu tamamlandı')}
      />

      {/* Help Overlay */}
      {showHelpOverlay && (
        <div className="fixed bottom-4 right-4 z-50 animate-fade-in">
          <div className="bg-slate-900/95 backdrop-blur-md rounded-lg p-4 border border-slate-700/50 shadow-xl text-sm max-w-xs">
            <div className="text-emerald-400 font-semibold mb-2">📋 Komutlar</div>
            <div className="space-y-1 text-slate-300">
              <div><span className="text-blue-400">!harcama tl [miktar]</span> - TL ekle</div>
              <div><span className="text-blue-400">!harcama durum</span> - Durumu göster</div>
              <div><span className="text-blue-400">!harcama undo</span> - Son işlemi geri al</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
